import { matchPath } from 'react-router-dom';

import {
  UserFeedbackActor,
  UserFeedbackCategory,
  UserFeedbackPageContext,
  UserFeedbackStatus,
} from './types';

const EXCLUDED_POD_LIKE_SEGMENTS = new Set([
  'admin',
  'admin-controls',
  'asset-preview',
  'creative-preview',
  'login',
  'monitoring',
  'old-creative-preview',
  'register',
  'update-password',
  'verify-email',
]);

const asTrimmedString = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

export const isFeedbackSupportedPath = (pathname: string) => {
  if (pathname === '/') return true;
  if (pathname.startsWith('/creative-preview')) return true;

  const segments = pathname.split('/').filter(Boolean);
  return (
    segments.length === 1 && !EXCLUDED_POD_LIKE_SEGMENTS.has(segments[0] || '')
  );
};

export const buildFeedbackPageContext = (
  pathname: string,
  search: string,
): UserFeedbackPageContext => {
  if (pathname === '/') {
    return {
      kind: 'listing',
      label: 'Listing Page',
      path: pathname,
      search,
      url: `${pathname}${search}`,
    };
  }

  if (pathname.startsWith('/creative-preview')) {
    return {
      kind: 'creative-preview',
      label: 'AdSure / Creative Preview',
      path: pathname,
      search,
      url: `${pathname}${search}`,
    };
  }

  const podMatch = matchPath<{ pod: string }>(pathname, {
    path: '/:pod',
    exact: true,
  });

  if (podMatch?.params?.pod) {
    return {
      kind: 'pod',
      label: `Pod Page - ${podMatch.params.pod}`,
      path: pathname,
      search,
      url: `${pathname}${search}`,
      podName: podMatch.params.pod,
    };
  }

  return {
    kind: 'other',
    label: 'Application Page',
    path: pathname,
    search,
    url: `${pathname}${search}`,
  };
};

export const mapSessionUserToFeedbackActor = (user: any): UserFeedbackActor => {
  const givenName = asTrimmedString(user?.givenName);
  const familyName = asTrimmedString(user?.familyName);
  const fullName = [givenName, familyName].filter(Boolean).join(' ');
  const name =
    asTrimmedString(user?.name) || fullName || asTrimmedString(user?.email) || 'Unknown User';

  return {
    id:
      asTrimmedString(user?.ums_id) ||
      asTrimmedString(user?.googleId) ||
      asTrimmedString(user?.sub) ||
      asTrimmedString(user?.email) ||
      'unknown-user',
    name,
    email: asTrimmedString(user?.email) || undefined,
    type: asTrimmedString(user?.type) || undefined,
  };
};

export const formatFeedbackDateTime = (value?: string | null) => {
  if (!value) return 'Not available';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    timeZoneName: 'short',
    year: 'numeric',
  }).format(date);
};

export const getFeedbackStatusVariant = (status: UserFeedbackStatus) =>
  status === 'resolved' ? 'success' : 'warning';

export const getFeedbackCategoryVariant = (category: UserFeedbackCategory) =>
  category === 'issue' ? 'danger' : 'info';

export const getFeedbackErrorMessage = (error: any, fallback: string) => {
  if (typeof error?.data?.error === 'string' && error.data.error.trim()) {
    return error.data.error;
  }

  if (typeof error?.error === 'string' && error.error.trim()) {
    return error.error;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return fallback;
};
